import { Stack, Typography } from '@mui/material';

import { AutoSaveList } from '../../components';
import ParentAndCaregiverFields from './ParentAndCaregiverFields';
import { EMPTY_FAMILY_MEMBER_VALUES } from '../constants';
import { FAMILY_ADD_BUTTON_SX, FAMILY_SECTION_HEADING_SX } from '../styles';
import { PARENT_CAREGIVER_FORM_SCHEMA } from '../validationSchema';
import {
  getFamilyMemberRowLabel,
  getFamilyRoleCoverageError,
  mapFamilyMemberToValues,
  toFamilyMemberFieldPayload,
} from '../utils';

import type { FamilyMemberFormValues, FamilyMemberPayload, IFamilyMember } from '../types';
import type { ParentAndCaregiverSectionProps } from './types';

export default function ParentAndCaregiverSection({
  familyMembers,
  disabled,
  isSaving,
  onCreate,
  onUpdate,
  onDelete,
}: ParentAndCaregiverSectionProps) {
  return (
    <Stack gap={3} textAlign="left">
      <Typography variant="h6" sx={FAMILY_SECTION_HEADING_SX}>
        Parents & caregivers (Make sure to include all parents and caregivers)
      </Typography>

      <AutoSaveList<FamilyMemberFormValues, IFamilyMember, FamilyMemberPayload>
        addButtonLabel="Add parent / caregiver"
        addButtonSx={FAMILY_ADD_BUTTON_SX}
        disabled={disabled}
        emptyValues={EMPTY_FAMILY_MEMBER_VALUES}
        entries={familyMembers}
        getDeleteConfirm={(row) => ({
          title: 'Confirm parent / caregiver deletion?',
          message: `You are about to delete "${row.full_name?.trim() || 'this parent / caregiver'}"`,
        })}
        getRowKey={(row) => row.id}
        getRowLabel={getFamilyMemberRowLabel}
        isSaving={isSaving}
        mapRowToValues={mapFamilyMemberToValues}
        newEntryLabel="New parent / caregiver"
        onCreate={onCreate}
        onDelete={onDelete}
        onUpdate={onUpdate}
        listErrorMessage={getFamilyRoleCoverageError(familyMembers)}
        requiredMessage="At least one parent and caregiver is required"
        renderFields={({ formik, disabled: rowDisabled, readOnly, saveField }) => (
          <ParentAndCaregiverFields
            disabled={rowDisabled}
            formik={formik}
            readOnly={readOnly}
            saveField={saveField}
          />
        )}
        toFieldPayload={toFamilyMemberFieldPayload}
        validationSchema={PARENT_CAREGIVER_FORM_SCHEMA}
      />
    </Stack>
  );
}
